<script>
import { mapActions, mapState } from 'vuex';
import { cloneDeep } from 'lodash-es';
import ValidatorMixin from '../../mixins/validatorMixin';
import ItineraryDayProgramImageModal from './ItineraryDayProgramImageModal';
import TwCopyAndTranslateModal from '../TwCopyAndTranslateModal';

export default {
  name: 'ItineraryDayProgramList',
  components: {
    TwCopyAndTranslateModal,
    ItineraryDayProgramImageModal,
  },
  mixins: [ ValidatorMixin ],
  props: {
    itineraryId: {
      type: Number,
      required: true,
    },
    dayProgramLoading: {
      type: Boolean,
      default: false,
    },
    dayProgramItems: {
      type: Array,
      default: () => [],
    },
    dayProgramError: {
      type: Boolean,
      default: false,
    },
    selectedDomain: {
      type: Object,
      default: null,
    },
    domains: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dayProgramEntries: [],
      dayProgramDomainEntries: [],
      headers: [
        { text: this.$t('itinerary.edit.dayProgram.table.days'), sortable: false },
        { text: this.$t('itinerary.edit.dayProgram.table.description'), sortable: false },
        { text: this.$t('itinerary.edit.dayProgram.table.dayText'), sortable: false },
        { text: this.$t('itinerary.edit.dayProgram.table.location'), sortable: false, align: 'center' },
        { text: this.$t('itinerary.edit.dayProgram.table.hotels'), sortable: false, align: 'center' },
        { text: 'Extras', sortable: false, align: 'center', isExtra: true },
        { text: this.$t('itinerary.edit.dayProgram.table.media'), sortable: false, align: 'center' },
        { text: this.$t('itinerary.edit.dayProgram.table.mealPlan'), sortable: false, align: 'center' },
        { text: this.$t('itinerary.edit.dayProgram.table.actions'), sortable: false, align: 'center' },
      ],
      toggled: [],
      valid: true,
      copyAndTranslateModalOpen: false,
      buttonToggleState: null,
    };
  },
  computed: {
    ...mapState({
      itineraryDomains: state => state.itinerary.domains.items,
      selectedExtraCategory: state => state.itinerary.selectedExtraCategory,
    }),
    getDayProgramCopyValues() {
      const copyItineraryDomainMapping = [];
      this.itineraryDomains.forEach(itineraryDomain => {
        copyItineraryDomainMapping[itineraryDomain.domainId] = [
          {
            column: 'itinerary_domain_id',
            value: itineraryDomain.id,
          },
        ];
      });
      const values = [];
      this.dayProgramEntries.forEach(dayProgram => {
        values.push({
          globalModelId: dayProgram.id,
          globalModelColumnName: 'itinerary_day_program_id',
          // We need an override for domain_id key on this since it references an itinerary_domain_id in the db
          domainKeyOverrides: copyItineraryDomainMapping,
          selectKeyOverrides: [ 'itinerary_day_program_id', 'itinerary_domain_id' ],
          tableName: 'itinerary_domain_day_programs',
          sourceDomainId: null,
          selectedFromDomain: null,
          fromLanguageIsoOverride: null,
          sourceIsoOverride: null,
          toDomains: [],
          shouldTranslate: false,
          shouldCopySeo: false,
          fieldNamesAndValuesToCopyAndTranslate: [
            { itinerary_domain_id: copyItineraryDomainMapping },
            { itinerary_day_program_id: dayProgram.id },
            { description: dayProgram.description },
            { teaser_text: dayProgram.teaserText },
            { text: dayProgram.text },
          ],
          title: `Day: ${dayProgram.day}`,
        });
      });
      return values;
    },
  },
  watch: {
    dayProgramItems() {
      this.dayProgramEntries = cloneDeep(this.dayProgramItems);
      this.toggled = new Array(this.dayProgramItems.length).fill(false);
    },
    selectedDomain(val) {
      if (val) {
        this.dayProgramDomainEntries = cloneDeep(this.selectedDomain.dayPrograms);
      } else {
        this.dayProgramDomainEntries = [];
      }
    },
  },
  created() {
    this.dayProgramEntries = cloneDeep(this.dayProgramItems);
    this.toggled = new Array(this.dayProgramItems.length).fill(false);
    if (this.selectedDomain) {
      this.dayProgramDomainEntries = cloneDeep(this.selectedDomain.dayPrograms);
    }
    this.getItineraryDomains(this.itineraryId);
  },
  methods: {
    ...mapActions({
      getItineraryDomains: 'itinerary/getItineraryDomains',
      setSelectedExtraCategory: 'itinerary/setSelectedExtraCategory',
    }),
    toggle(index) {
      this.toggled[index] = !this.toggled[index];
      this.toggled = [ ...this.toggled ];
    },
    updateEntry(event) {
      this.dayProgramEntries[event.index][event.model] = event.value;
    },
    updateDomainEntry(event) {
      const dayProgramEntry = this.dayProgramEntries[event.index];
      const dayProgramDomainIndex = this.dayProgramDomainEntries
        .map(dayProgram => dayProgram.itineraryDayProgramId)
        .indexOf(dayProgramEntry.id);
      this.dayProgramDomainEntries[dayProgramDomainIndex][event.model] = event.value;
    },
    getDayProgramDomain(dayProgramId) {
      let dayProgramDomain = this.dayProgramDomainEntries
        .filter(dayProgram => dayProgram.itineraryDayProgramId === dayProgramId)[0]
        || null;
      if (!dayProgramDomain) {
        dayProgramDomain = {
          itineraryDomainId: this.selectedDomain.id,
          itineraryDayProgramId: dayProgramId,
          description: null,
          teaserText: null,
          text: null,
        };
        this.dayProgramDomainEntries.push(dayProgramDomain);
      }
      return dayProgramDomain;
    },
    save() {
      this.dayProgramEntries.forEach((dayProgramEntry, index) => {
        this.$set(this.dayProgramEntries, index, {
          ...dayProgramEntry,
          day: index + 1,
        });
      });
      this.$emit('saveDayProgram', this.dayProgramEntries);
      if (this.selectedDomain) {
        this.$emit('updateDomain', {
          ...this.selectedDomain,
          dayPrograms: this.dayProgramDomainEntries,
        });
      }
    },
    addDayProgram() {
      this.$emit('addDayProgramEntry', {
        itineraryId: this.itineraryId,
        day: null,
      });
      this.save();
    },
    getExtraCount(day) {
      return day.extras.filter(extra =>
        extra.type === 'optional' || extra.type === 'included').length;
    },
    reorderDayProgramEntries(dayProgramId, direction) {
      const index = this.dayProgramEntries.findIndex(dayProgramEntry => dayProgramEntry.id === dayProgramId);
      if (index === -1) return;

      if (direction === 'up' && index > 0) {
        /* eslint-disable max-len */
        [ this.dayProgramEntries[index - 1], this.dayProgramEntries[index] ] = [ this.dayProgramEntries[index], this.dayProgramEntries[index - 1] ];
        this.dayProgramEntries[index - 1].day -= 1;
        this.dayProgramEntries[index].day += 1;
      } else if (direction === 'down' && index < this.dayProgramEntries.length - 1) {
        [ this.dayProgramEntries[index + 1], this.dayProgramEntries[index] ] = [ this.dayProgramEntries[index], this.dayProgramEntries[index + 1] ];
        this.dayProgramEntries[index + 1].day += 1;
        this.dayProgramEntries[index].day -= 1;
      }
      this.save();
    },
    handleOpenModalEmit(dayProgram) {
      switch (this.buttonToggleState) {
        case 'golf':
          this.$emit('openGolfModal', dayProgram);
          break;
        case 'excursion':
        case 'transfer':
          this.$emit('openExtraModal', dayProgram);
          break;
        default:
          break;
      }
    },
  },
};
</script>

<template>
  <v-container class="pa-0 fluid">
    <v-layout row wrap>
      <v-flex class="my-4">
        <v-form v-model="valid">
          <v-data-table
            :items="dayProgramEntries"
            :headers="headers"
            :loading="dayProgramLoading"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:headers="props">
              <th
                v-for="header in props.headers"
                :class="header.align"
                :key="header.text"
                :style="header.style"
                :width="header.width"
              >
                <template v-if="header.isExtra">
                  <span>
                    <v-btn-toggle v-model="buttonToggleState" mandatory @change="setSelectedExtraCategory">
                      <v-btn value="golf" depressed @click="$emit('openGolfModal', dayProgramEntries[0])">
                        G
                      </v-btn>
                      <v-btn value="excursion" depressed>
                        E
                      </v-btn>
                      <v-btn value="transfer" depressed>
                        T
                      </v-btn>
                    </v-btn-toggle>
                  </span>
                </template>
                <template v-else>
                  {{ header.text }}
                </template>
              </th>
            </template>
            <template v-slot:items="props">
              <tr>
                <td>{{ $t('itinerary.edit.dayProgram.dayIterator', [props.item.day]) }}</td>
                <td>
                  <v-text-field
                    v-model="props.item.description"
                    :label="$t('itinerary.edit.dayProgram.table.description')"
                    :rules="[
                      rules.requiredWith.bind(this, props.item.countryId),
                    ]"
                    hide-details
                    class="my-3"
                  />
                  <v-text-field
                    v-if="selectedDomain"
                    :value="getDayProgramDomain(props.item.id).description"
                    :label="$t('itinerary.edit.dayProgram.table.localDescription')"
                    hide-details
                    class="my-3"
                    @input="updateDomainEntry(
                      {model: 'description', value: $event, index: props.index}
                    )"
                  />
                </td>
                <td>
                  <template>
                    <v-btn
                      :color="props.item.teaserText && props.item.text ? 'success' : 'default'"
                      fab
                      small
                      depressed
                      @click="$emit('openDayTextModal', props.item)"
                    >
                      <v-icon>book</v-icon>
                    </v-btn>
                  </template>
                </td>
                <td class="text-xs-center">
                  <template v-if="props.item.countryId">
                    <v-btn
                      small
                      depressed
                      color="primary"
                      @click="$emit('openLocationModal', props.item)"
                    >
                      {{ props.item.country }} - {{ props.item.area }}
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      :disabled="!props.item.description"
                      fab
                      small
                      depressed
                      color="primary"
                      @click="$emit('openLocationModal', props.item)"
                    >
                      <v-icon>map</v-icon>
                    </v-btn>
                  </template>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    :disabled="!props.item.countryId"
                    :color="props.item.hotels.length ? 'primary' : 'warning'"
                    fab
                    small
                    depressed
                    @click="$emit('openHotelModal', props.item)"
                  >
                    <template v-if="props.item.hotels.length">
                      {{ props.item.hotels.length }}
                    </template>
                    <template v-else>
                      <v-icon>hotel</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    :disabled="!props.item.countryId"
                    :color="getExtraCount(props.item) ? 'primary' : 'warning'"
                    fab
                    small
                    depressed
                    @click="handleOpenModalEmit(props.item)"
                  >
                    <template v-if="getExtraCount(props.item)">
                      {{ getExtraCount(props.item) }}
                    </template>
                    <template v-else>
                      <v-icon>directions_walk</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    :color="props.item.images.length ? 'primary' : 'warning'"
                    fab
                    small
                    depressed
                    @click="$emit('openImageModal', props.item)"
                  >
                    <template v-if="props.item.images.length">
                      {{ props.item.images.length }}
                    </template>
                    <template v-else>
                      <v-icon>collections</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    :color="props.item.mealPlans.length ? 'primary' : 'warning'"
                    fab
                    small
                    depressed
                    @click="$emit('openMealPlanModal', props.item)"
                  >
                    <template v-if="props.item.mealPlans.length">
                      {{ props.item.mealPlans.length }}
                    </template>
                    <template v-else>
                      <v-icon>fastfood</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    :disabled="props.index === 0"
                    fab
                    small
                    depressed
                    color="secondary"
                    @click="reorderDayProgramEntries(props.item.id, 'up')"
                  >
                    <v-icon>arrow_upward</v-icon>
                  </v-btn>
                  <v-btn
                    :disabled="props.index === dayProgramEntries.length - 1"
                    fab
                    small
                    depressed
                    color="secondary"
                    @click="reorderDayProgramEntries(props.item.id, 'down')"
                  >
                    <v-icon>arrow_downward</v-icon>
                  </v-btn>
                  <v-btn
                    fab
                    small
                    depressed
                    color="primary"
                    @click="$emit('duplicateDayProgramEntry', props.item)"
                  >
                    <v-icon>copy_all</v-icon>
                  </v-btn>
                  <v-btn
                    fab
                    small
                    depressed
                    color="error"
                    @click="$emit('deleteDayProgramEntry', props.item.id)"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
          <v-btn
            :loading="dayProgramLoading"
            color="primary success"
            class="mt-4"
            @click="copyAndTranslateModalOpen = !copyAndTranslateModalOpen"
          >
            Copy/translate
          </v-btn>
          <tw-copy-and-translate-modal
            v-if="copyAndTranslateModalOpen"
            :elements-to-handle="getDayProgramCopyValues"
            :domains="domains"
          />
          <v-btn
            :loading="dayProgramLoading"
            color="success"
            class="right mr-4 mt-4"
            @click="addDayProgram"
          >
            {{ $t('buttons.add') }}
            <v-icon class="ml-1">add</v-icon>
          </v-btn>
          <v-btn
            :loading="dayProgramLoading"
            color="primary"
            class="right mr-4 mt-4"
            @click="save"
          >
            {{ $t('buttons.save') }}
            <v-icon class="ml-1">save</v-icon>
          </v-btn>
        </v-form>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style lang="scss" scoped>
  @import '../../assets/scss/variables.scss';

  .expanded-row {
    position: relative;
    display: table-row;
    height: $expanded-row-height;
    width: 100%;
    overflow: hidden;
  }

  .expanded-row-input {
    margin-top: $margin-lg;
    margin-left: $margin-lg;
    width: $expanded-row-input-width;
  }

  .expanded-row-data {
    position: absolute;
    width: 100%;
    height: $expanded-row-height;
    overflow: auto;
  }

  .expanded-row-form {
    width: 50%;
    float: right;
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryDayProgramList.vue